import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import MediaSelector from "../editor/fields/MediaSelector";

export const ComparisonItem = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &.top {
    z-index: 2;
  }
`;

export const Handle = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 3px;
  height: 100%;
  background: white;
  z-index: 3;
  cursor: col-resize;
  top: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  & > div {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    border-radius: 8px;
    padding: 6px;
    display: flex;
    gap: 4px;
  }

  img {
    width: 10px;
    height: 10px;
  }
`;

export default function ComparisonSlider({ leftImage, rightImage, rightImageText, leftImageText }) {
  const [isResizing, setIsResizing] = useState(false);
  const topImageRef = useRef();
  const handleRef = useRef();

  const setPositioning = useCallback((x) => {
    const { left, width } = topImageRef.current.getBoundingClientRect();
    const handleWidth = handleRef.current.offsetWidth;

    if (x >= left && x <= left + width - handleWidth) {
      const percentage = ((x - left) / width) * 100;
      handleRef.current.style.left = `${percentage}%`;
      handleRef.current.style.transform = `translateX(-50%)`;
      topImageRef.current.style.clipPath = `inset(0 ${100 - percentage}% 0 0)`;
    }
  }, []);

  const handleResize = useCallback(
    (e) => {
      if (e.clientX) {
        setPositioning(e.clientX);
      } else if (e.touches?.[0]?.clientX) {
        setPositioning(e.touches[0].clientX);
      }
    },
    [setPositioning]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    window.removeEventListener("mousemove", handleResize);
    window.removeEventListener("touchmove", handleResize);
    window.removeEventListener("mouseup", handleResizeEnd);
    window.removeEventListener("touchend", handleResizeEnd);
  }, [handleResize]);

  const onKeyDown = useCallback(
    (e) => {
      const { offsetLeft, offsetParent } = handleRef.current;
      const current = offsetLeft + offsetParent.offsetLeft;
      if (e.code === "ArrowLeft") {
        setPositioning(current - 10);
      }
      if (e.code === "ArrowRight") {
        setPositioning(current + 10);
      }
    },
    [setPositioning]
  );

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", handleResize);
      window.addEventListener("touchmove", handleResize);
      window.addEventListener("mouseup", handleResizeEnd);
      window.addEventListener("touchend", handleResizeEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleResize);
      window.removeEventListener("touchmove", handleResize);
      window.removeEventListener("mouseup", handleResizeEnd);
      window.removeEventListener("touchend", handleResizeEnd);
    };
  }, [isResizing, handleResize, handleResizeEnd]);

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  useEffect(() => {
    // Center slider on load
    const { left, width } = topImageRef.current.getBoundingClientRect();
    const handleWidth = handleRef.current.offsetWidth;
    setPositioning(width / 2 + left - handleWidth / 2);
  }, [setPositioning]);

    const top = leftImage? leftImage : Window.static_path + 'img/other/lumber-pile.jpg'
    const bottom = rightImage? rightImage: Window.static_path + 'img/other/couple.jpg'

  return (
    <div className="mx-auto position-relative overflow-hidden w-100 ratio ratio-16x9" style={{boxShadow: "0 15px 15px rgb(0 0 0 / 25%)"}}>
      <ComparisonItem>
        <img src={bottom} alt="Bottom image" className="w-100 h-100 align-middle user-select-none pe-none" style={{objectFit:"cover"}} />
        {rightImageText && <p className="position-absolute top-0 end-0 text-white p-2 bg-dark bg-opacity-50 rounded n-fs-7 mb-0">{rightImageText}</p>}
      </ComparisonItem>
      <ComparisonItem ref={topImageRef} className="top">
        <img src={top} alt="Top image" className="w-100 h-100 align-middle user-select-none pe-none" style={{objectFit:"cover"}} />
        {leftImageText && <p className="position-absolute top-0 start-0 text-white p-2 bg-dark bg-opacity-50 rounded n-fs-7 mb-0">{leftImageText}</p>}
      </ComparisonItem>
      <Handle
        ref={handleRef}
        onMouseDown={() => setIsResizing(true)}
        onTouchStart={() => setIsResizing(true)}
      >
        <div>
          <img src={`${Window.static_path}img/icons/arrow-black.svg`} alt="Arrow Left" />
          <img src={`${Window.static_path}img/icons/arrowBlack.svg`} alt="Arrow Right" />
        </div>
      </Handle>
    </div>
  );
}

ComparisonSlider.defaultProps={
    leftImage: '',
    leftImageText: 'Left Image',
    rightImage: '',
    rightImageText: 'Right image',
}

ComparisonSlider.fields = { 
    leftImage: {
        type: "custom",
        render: ({ name, onChange, value }) => <MediaSelector name={name} onChange={onChange} value={value} />,
    },
    leftImageText: {
      type:"text"
    },
    rightImage: {
      type: "custom",
      render: ({ name, onChange, value }) => <MediaSelector name={name} onChange={onChange} value={value} />,
  },
  rightImageText: {
    type:"text"
  },
}
